/**
 * @module components/CustomerAutocompleteSearch
 * @description Server-side Customer-Autocomplete für Klärungs-Wizard + Convert-Modal.
 *   200ms-debounced ILIKE %q% gegen /api/kunden?q=…&limit=20, Starts-With-Priorität,
 *   max-height 320px, Click+Enter/Esc-Support.
 *
 * #1240 — Ersatz für native <select> in ClaimsFormular.jsx + ConvertCaseToClaimModal.
 *
 * @since v2.536.0
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

export default function CustomerAutocompleteSearch({
  value,            // selected kunde_id (string|number)
  selectedLabel,    // selected label (for initial display when value set without options)
  onChange,         // (id, kundeRecord) => void
  placeholder = 'Kunde suchen…',
  disabled = false,
  className = '',
  emptyText = 'Keine Treffer',
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [displayLabel, setDisplayLabel] = useState(selectedLabel || '');

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (selectedLabel) setDisplayLabel(selectedLabel);
  }, [selectedLabel]);

  // Wenn value extern auf '' gesetzt wird (z.B. Form-Reset) → Label leeren
  useEffect(() => {
    if (!value) setDisplayLabel('');
  }, [value]);

  // Initial load wenn open + leerer Query → top 20 alphabetisch
  const doSearch = useCallback(async (q) => {
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const url = `${API}/api/kunden?limit=20${q ? `&q=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, { credentials: 'include', signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setResults(Array.isArray(data) ? data.filter(k => k.aktiv !== false) : []);
      setActiveIdx(0);
    } catch (e) {
      if (e.name !== 'AbortError') {
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced Search on Query-Change
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch(query);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, doSearch]);

  // Initial fetch on first open
  useEffect(() => {
    if (open && results.length === 0 && !query) {
      doSearch('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Outside-Click schließt
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Auto-Focus Input
  useEffect(() => {
    if (open && inputRef.current) {
      const id = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Scroll active item into view
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIdx, open]);

  const handleSelect = useCallback((kunde) => {
    setDisplayLabel(kunde.name);
    onChange?.(String(kunde.id), kunde);
    setOpen(false);
    setQuery('');
  }, [onChange]);

  const handleClear = useCallback((e) => {
    e?.stopPropagation();
    setDisplayLabel('');
    onChange?.('', null);
    setQuery('');
  }, [onChange]);

  const handleKeyDown = useCallback((e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = results[activeIdx];
      if (opt) handleSelect(opt);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setQuery('');
    }
  }, [open, results, activeIdx, handleSelect]);

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={handleKeyDown}
        className={`w-full px-3 py-2 text-[14px] rounded-[8px] border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)] flex items-center justify-between gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--bg-inset,#F8F9FC)]'} ${required && !value ? 'border-[#F59E0B]' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate text-left flex-1 ${displayLabel ? '' : 'text-[var(--text-tertiary,#6B7280)]'}`}>
          {displayLabel || placeholder}
        </span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {displayLabel && !disabled && (
            <X
              className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]"
              onClick={handleClear}
            />
          )}
          <ChevronDown className={`w-4 h-4 text-[var(--text-tertiary,#6B7280)] transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-[90] mt-1 w-full bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-[10px] shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
            <Search className="w-3.5 h-3.5 text-[var(--text-tertiary,#6B7280)] flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="w-full bg-transparent outline-none text-[13px] text-[var(--text-primary,#1A1A2E)] placeholder:text-[var(--text-tertiary,#6B7280)]"
            />
            {loading && <span className="text-[11px] text-[var(--text-tertiary,#6B7280)]">…</span>}
          </div>
          <div ref={listRef} className="max-h-[320px] overflow-y-auto" role="listbox">
            {results.length === 0 && !loading ? (
              <div className="px-3 py-4 text-[13px] text-[var(--text-tertiary,#6B7280)] text-center">{emptyText}</div>
            ) : (
              results.map((k, idx) => {
                const isSelected = String(k.id) === String(value ?? '');
                const isActive = idx === activeIdx;
                return (
                  <div
                    key={k.id}
                    data-idx={idx}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(k); }}
                    className={`px-3 py-2 text-[13px] cursor-pointer flex items-center justify-between gap-2 ${isActive ? 'bg-[var(--bg-inset,#F8F9FC)]' : ''} ${isSelected ? 'text-[#338edc] font-medium' : 'text-[var(--text-primary,#1A1A2E)]'}`}
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate">{k.name}</span>
                      {k.standort_name && <span className="text-[11px] text-[var(--text-tertiary,#6B7280)] truncate">{k.standort_name}</span>}
                    </div>
                    {isSelected && <span className="text-[#338edc] flex-shrink-0">✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
